import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useLocation, Outlet } from 'react-router-dom'
import {
  LayoutDashboard,
  Users,
  CreditCard,
  ShoppingCart,
  LogOut,
  ImageIcon,
  Dumbbell,
  MessageSquareQuote,
  Video,
  ChevronsLeft,
  ChevronsRight,
  User,
  ChevronDown,
  Settings,
  Database,
  FileText,
  Code,
  Calendar,
  CheckCircle,
  Shield,
  Clock,
  UserCheck,
  Monitor,
  Star,
  Play,
  FileImage,
  HelpCircle,
  TrendingUp,
  Utensils,
} from 'lucide-react'

export default function DashboardLayout() {
  const navigate = useNavigate()
  const location = useLocation()
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const [userName, setUserName] = useState('')
  const [userEmail, setUserEmail] = useState('')
  const [role, setRole] = useState('')
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false)
  const [planManagementOpen, setPlanManagementOpen] = useState(false)
  const [usersManagementOpen, setUsersManagementOpen] = useState(false)
  const [masterDataManagementOpen, setMasterDataManagementOpen] = useState(false)
  const [trainerManagementOpen, setTrainerManagementOpen] = useState(false)
  const [contentManagementOpen, setContentManagementOpen] = useState(false)

  // Collapsed sidebar submenu state
  const [collapsedSubmenu, setCollapsedSubmenu] = useState(null) // { item, top }
  const closeTimeoutRef = useRef(null)
  const sidebarRef = useRef(null)
  const panelRef = useRef(null)

  useEffect(() => {
    // Updated to use the correct localStorage keys from LoginPage
    const storedUserName = localStorage.getItem('firstName')
    const storedUserEmail = localStorage.getItem('email')
    const storedRole = localStorage.getItem('role')
    if (storedUserName) setUserName(storedUserName)
    if (storedUserEmail) setUserEmail(storedUserEmail)
    if (storedRole) setRole(storedRole)
  }, [])

  const handleLogout = () => {
    localStorage.removeItem('userId')
    localStorage.removeItem('username')
    localStorage.removeItem('email')
    localStorage.removeItem('firstName')
    localStorage.removeItem('lastName')
    localStorage.removeItem('role')
    localStorage.removeItem('accessToken')
    localStorage.removeItem('refreshToken')
    localStorage.removeItem('tokenType')
    window.dispatchEvent(new Event('auth-changed'))
    navigate('/')
  }

  const toggleSidebar = () => setSidebarOpen(!sidebarOpen)
  const toggleProfileDropdown = () => setProfileDropdownOpen(!profileDropdownOpen)

  const clearCloseTimeout = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current)
      closeTimeoutRef.current = null
    }
  }

  const scheduleCloseCollapsedSubmenu = () => {
    clearCloseTimeout()
    closeTimeoutRef.current = setTimeout(() => setCollapsedSubmenu(null), 150)
  }

  const openCollapsedSubmenu = (item, e) => {
    clearCloseTimeout()
    // Position near the hovered/clicked item
    const rect = e.currentTarget.getBoundingClientRect()
    const estimatedHeight = (item.subItems?.length || 0) * 42 + 16
    let top = rect.top + window.scrollY
    const maxTop = window.innerHeight + window.scrollY - estimatedHeight - 16
    if (top > maxTop) top = Math.max(8, maxTop)
    setCollapsedSubmenu({ item, top })
  }

  const closeCollapsedSubmenu = () => {
    clearCloseTimeout()
    setCollapsedSubmenu(null)
  }

  useEffect(() => {
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target) && sidebarRef.current && !sidebarRef.current.contains(e.target)) {
        setCollapsedSubmenu(null)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const menuItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard', type: 'single' },
    ...(role === 'SUPER_ADMIN' ? [{ icon: Shield, label: 'Admin Management', path: '/dashboard/admin-management', type: 'single' }] : []),
    {
      icon: CreditCard,
      label: 'Plan Management',
      type: 'dropdown',
      isOpen: planManagementOpen,
      setOpen: setPlanManagementOpen,
      subItems: [
        { icon: Monitor, label: 'Subscription Plans', path: '/dashboard/subscriptions' },
        { icon: FileText, label: 'Addon Plans', path: '/dashboard/addonplans' },
        { icon: Calendar, label: 'Addon Sessions', path: '/dashboard/addonsessions' },
      ]
    },
    {
      icon: Users,
      label: 'Users Management',
      type: 'dropdown',
      isOpen: usersManagementOpen,
      setOpen: setUsersManagementOpen,
      subItems: [
        { icon: User, label: 'Users', path: '/dashboard/users' },
        { icon: UserCheck, label: 'Subscribers', path: '/dashboard/subscribers' },
      ]
    },
    {
      icon: Database,
      label: 'Master Data Management',
      type: 'dropdown',
      isOpen: masterDataManagementOpen,
      setOpen: setMasterDataManagementOpen,
      subItems: [
        { icon: Database, label: 'Master Data', path: '/dashboard/masterdata' },
        { icon: Code, label: 'Master Data Rules', path: '/dashboard/masterdatarules' },
        { icon: Utensils, label: 'Food Macros', path: '/dashboard/food-macros' },
        { icon: TrendingUp, label: 'Diet Combinations', path: '/dashboard/diet-combinations' },
      ]
    },
    {
      icon: Dumbbell,
      label: 'Trainer Management',
      type: 'dropdown',
      isOpen: trainerManagementOpen,
      setOpen: setTrainerManagementOpen,
      subItems: [
        { icon: UserCheck, label: 'Trainer', path: '/dashboard/trainer' },
        { icon: Clock, label: 'Trainer Time Slots', path: '/dashboard/trainer-time-slots' },
        { icon: UserCheck, label: 'Booked Sessions', path: '/dashboard/booked-sessions' },
      ]
    },
    {
      icon: FileImage,
      label: 'Content Management',
      type: 'dropdown',
      isOpen: contentManagementOpen,
      setOpen: setContentManagementOpen,
      subItems: [
        { icon: ImageIcon, label: 'Banner', path: '/dashboard/banner' },
        { icon: Star, label: 'General Testimonials', path: '/dashboard/testomonial' },
        { icon: MessageSquareQuote, label: 'User Testimonials', path: '/dashboard/user-testimonials' },
        { icon: Play, label: 'Global Video', path: '/dashboard/globalvideo' },
        { icon: FileText, label: 'Diet Charts', path: '/dashboard/dietcharts' },
      ]
    },
    { icon: CheckCircle, label: 'Approval Requests', path: '/dashboard/approval-requests', type: 'single' },
    { icon: Shield, label: 'Google OAuth2', path: '/dashboard/google-oauth2', type: 'single' },
    { icon: ShoppingCart, label: 'Orders', path: '/dashboard/orders', type: 'single' },
    { icon: Settings, label: 'Settings', path: '/dashboard/settings', type: 'single' },
    { icon: HelpCircle, label: 'Help & Support', path: '/dashboard/help-support', type: 'single' },
  ]

  // Find active item including sub-items
  const getActiveItem = () => {
    // Check single items first
    const singleItem = menuItems.find(item => item.type === 'single' && item.path === location.pathname)
    if (singleItem) return singleItem.label

    // Check dropdown sub-items
    for (const item of menuItems) {
      if (item.type === 'dropdown' && item.subItems) {
        const subItem = item.subItems.find(sub => sub.path === location.pathname)
        if (subItem) return subItem.label
      }
    }
    return 'Dashboard'
  }

  const activeItem = getActiveItem()

  return (
    <div className="flex h-screen bg-black">
      {/* Sidebar */}
      <div ref={sidebarRef} className={`${sidebarOpen ? 'w-60' : 'w-16'
        } bg-black transition-all duration-300 ease-in-out flex flex-col`}
      >
        {/* Sidebar Header */}
        <div className="flex items-center justify-between">
          {/* <div className="flex items-center"> */}
          {sidebarOpen && (
            <span className="text-2xl font-bold gradient-text colorofte px-4 py-2.5">BYB</span>
          )}
          {/* </div> */}
          <button
            onClick={toggleSidebar}
            className="text-white hover:text-blue-500 focus:outline-none py-3.5 px-5"
          >
            {sidebarOpen ? <ChevronsLeft className="w-6 h-6" /> : <ChevronsRight className="w-6 h-6" />}
          </button>
        </div>

        {/* Sidebar Items */}
        <div className="flex-1 overflow-y-auto text-white bg-black">
          {menuItems.map((item, index) => (
            <div key={index}>
              {item.type === 'single' ? (
                <div
                  className={`flex items-center px-3 py-2 cursor-pointer hover:bg-blue-500 ${activeItem === item.label ? 'bg-blue-600 font-medium' : ''
                    } ${!sidebarOpen ? 'justify-center' : ''}`}
                  onClick={() => navigate(item.path)}
                  title={item.label}
                >
                  <item.icon className="w-5 h-5 text-white" />
                  {sidebarOpen && <span className="ml-2 text-sm">{item.label}</span>}
                </div>
              ) : (
                // Dropdown menu
                <div>
                  <div
                    className={`flex items-center px-3 py-2 cursor-pointer hover:bg-blue-500 ${item.subItems?.some(sub => sub.path === location.pathname) ? 'bg-blue-600' : ''
                      } ${!sidebarOpen ? 'justify-center' : 'justify-between'}`}
                    onMouseEnter={(e) => { if (!sidebarOpen) openCollapsedSubmenu(item, e) }}
                    onMouseLeave={() => { if (!sidebarOpen) scheduleCloseCollapsedSubmenu() }}
                    onClick={(e) => {
                      if (!sidebarOpen) {
                        // When collapsed, open floating submenu instead of auto-navigating
                        openCollapsedSubmenu(item, e)
                      } else {
                        // When expanded, toggle dropdown
                        // Close other dropdowns
                        if (item.label !== 'Plan Management') setPlanManagementOpen(false)
                        if (item.label !== 'Users Management') setUsersManagementOpen(false)
                        if (item.label !== 'Master Data Management') setMasterDataManagementOpen(false)
                        if (item.label !== 'Trainer Management') setTrainerManagementOpen(false)
                        if (item.label !== 'Content Management') setContentManagementOpen(false)
                        // Toggle current dropdown
                        item.setOpen(!item.isOpen)
                      }
                    }}
                    title={sidebarOpen ? item.label : `${item.label} - Click to view sub-items`}
                  >
                    <div className="flex items-center">
                      <item.icon className="w-5 h-5 text-white" />
                      {sidebarOpen && <span className="ml-2 text-sm">{item.label}</span>}
                    </div>
                    {sidebarOpen && (
                      <ChevronDown
                        className={`w-3 h-3 text-white transition-transform duration-200 ${item.isOpen ? 'rotate-180' : ''
                          }`}
                      />
                    )}
                  </div>

                  {/* Dropdown Sub-items */}
                  {item.isOpen && sidebarOpen && (
                    <div className="bg-gray-800">
                      {item.subItems?.map((subItem, subIndex) => (
                        <div
                          key={subIndex}
                          className={`flex items-center px-6 py-1.5 cursor-pointer hover:bg-blue-500 text-xs ${location.pathname === subItem.path ? 'bg-blue-600 font-medium' : ''
                            }`}
                          onClick={() => navigate(subItem.path)}
                          title={subItem.label}
                        >
                          <subItem.icon className="w-3 h-3 text-white mr-2" />
                          <span>{subItem.label}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Sidebar Footer */}
        {sidebarOpen && (
          <div className="border-t border-gray-700 p-3">
            {/* Version */}
            <div className="text-center mb-3">
              <span className="text-xs text-gray-400">v1.0.0</span>
            </div>

            {/* Logout Button */}
            <button
              onClick={handleLogout}
              className="w-full flex items-center justify-center px-3 py-2 text-sm text-white hover:bg-red-600 rounded transition-colors duration-200"
              title="Logout"
            >
              <LogOut className="w-4 h-4 mr-2" />
              <span>Logout</span>
            </button>

            {/* Developer Credit */}
            <div className="text-center mt-2">
              <span className="text-xs text-gray-500">Developed by TCY Consulting</span>
            </div>
          </div>
        )}

        {/* Collapsed Mode - Only Logout Icon */}
        {!sidebarOpen && (
          <div className="border-t border-gray-700 p-2">
            <button
              onClick={handleLogout}
              className="w-full flex items-center justify-center p-2 text-white hover:bg-red-600 rounded transition-colors duration-200"
              title="Logout"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        )}
      </div>

      {/* Collapsed Submenu Floating Panel */}
      {!sidebarOpen && collapsedSubmenu && (
        <div
          ref={panelRef}
          onMouseEnter={clearCloseTimeout}
          onMouseLeave={scheduleCloseCollapsedSubmenu}
          style={{ position: 'fixed', left: '64px', top: `${collapsedSubmenu.top}px` }}
          className="bg-gray-800 text-white rounded-md shadow-lg border border-gray-700 z-50 min-w-[14rem]"
        >
          <div className="p-2">
            {collapsedSubmenu.item.subItems?.map((subItem, idx) => (
              <div
                key={idx}
                onClick={() => { navigate(subItem.path); setCollapsedSubmenu(null) }}
                className={`flex items-center px-4 py-2 cursor-pointer hover:bg-blue-500 ${location.pathname === subItem.path ? 'bg-blue-600 font-medium' : ''}`}
              >
                <subItem.icon className="w-4 h-4 mr-3" />
                <span className="text-sm">{subItem.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col">
        {/* Top Header with User Profile */}
        <div className="bg-black px-2 py-1 flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold gradient-text colorofte">{activeItem}</h2>
          </div>

          {/* User Profile Section */}
          <div className="relative">
            <button
              onClick={toggleProfileDropdown}
              className="flex items-center space-x-3 bg-gray-50 hover:bg-gray-100 rounded-lg px-1 py-1 transition-colors duration-200"
            >
              <div className="w-8 h-8 rounded-full flex items-center justify-center">
                <User className="w-5 h-5 text-black" />
              </div>
              <div className="text-left">
                <p className="text-sm font-medium text-gray-900">{userName || 'User'}</p>
                <p className="text-xs text-gray-500">{role || 'Role'}</p>
              </div>
              <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${profileDropdownOpen ? 'rotate-180' : ''}`} />
            </button>

            {/* Profile Dropdown */}
            {profileDropdownOpen && (
              <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                <div className="px-4 py-3 border-b border-gray-100">
                  <p className="text-sm font-medium text-gray-900">{userName || 'User'}</p>
                  <p className="text-sm text-gray-500">{userEmail || 'user@example.com'}</p>
                </div>

                <div className="border-t border-gray-100 mt-2 pt-2">
                  <button
                    className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center"
                    onClick={handleLogout}
                  >
                    <LogOut className="w-4 h-4 mr-3" />
                    Log Out
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-auto p-2 bg-white">
          <Outlet />
        </div>
      </div>
    </div>
  )
}